import _ from "lodash";
import type { Moment } from "moment";
import moment, { isMoment } from "moment";
import { capitalize } from "./strings";
import type {
  TodoListByGroupKey,
  GroupKey,
  IdentifiableTodoPartial,
  Todo,
  TodoId,
  TodoIdMap,
  TodoUpdateDelta,
  TodoUpdateDeltaIdMap,
  TreeTodo,
} from "../types/todo";
import { isHashtagMetaItem } from "../types/todo";
import type { UserId } from "@common/types/user";
import type { SpaceId } from "@common/types/space";

// import '../tests/main'
const uuid = require("uuid");
import lexi from "lexicographic-integer";
import TodoGroupUtils, {
  PRESET_GROUP_KEYS,
} from "@common/utils/todo-group-utils";
import type { HashtagMeta } from "@web/src/api/model/hashtag-model";
import { isDefined } from "@common/utils/type-utils";

export const DISPLAY_MODES = [
  "default",
  "week",
  "calendar",
  "zen",
  "hashtag",
] as const;
export const isDateBasedGroup = (displayMode: DisplayMode) =>
  displayMode !== "hashtag";
export type DisplayMode = (typeof DISPLAY_MODES)[number];
export const directions = ["right", "left", "up", "down"] as const;
export type Direction = (typeof directions)[number];
export type FilterFunction = (todo: Todo) => {
  passPositiveFilter: boolean;
  passNegativeFilter: boolean;
  hasPositiveFilter: boolean;
  hasNegativeFilter: boolean;
};
export type CalendarModeOptions = {
  referenceDate: Date;
  firstDayInWeek: "monday" | "sunday";
};

export const nullFilterFn = () => ({
  passPositiveFilter: true,
  passNegativeFilter: true,
  hasNegativeFilter: false,
  hasPositiveFilter: false,
});
export const INIT_LEX_IDX = 5000; //we init the index at 5000, so it gives room for before and after
export const DEFAULT_LEX_IDX_OFFSET = 100; //we init the index at 5000, so it gives room for before and after
export const WEEK_FORECAST_DAYS = 7;
//========================================================================
//
//                            CONSTRUCTORS
//
//========================================================================
/**
 * merge delta id map on a todo delta level
 * if a todo did not exist
 * - if it is a full todo, add it to the map
 * - if it is a partial todo, ignore it
 */
export function mergeLeftIdMaps(
  oldTodoIdMap: TodoIdMap,
  deltaIdMap: TodoUpdateDeltaIdMap,
): TodoIdMap {
  const mergedIdMap = _.cloneDeep(oldTodoIdMap);
  for (let [todoId, delta] of Object.entries(deltaIdMap)) {
    // a stray is a PARTIAL todo that does not exist in original
    if (!mergedIdMap[todoId] && isFullTodo(delta)) {
      mergedIdMap[todoId] = delta;
      continue;
    }

    if (!mergedIdMap[todoId] && !isFullTodo(delta)) {
      // TODO: technically if oldTodoIdMap is of type TodoUpdateDeltaIdMap,
      // then partials are considered valid and not stray.
      console.warn("stray todo found:", delta);
      continue;
    }

    mergedIdMap[todoId] = mergeTodo(mergedIdMap[todoId]!, delta);
  }

  return mergedIdMap;
}
export function mergeLeftPartialIdMaps(
  oldTodoIdMap: TodoUpdateDeltaIdMap,
  deltaIdMap: TodoUpdateDeltaIdMap,
): TodoUpdateDeltaIdMap {
  const mergedIdMap = _.cloneDeep(oldTodoIdMap);
  for (let [todoId, delta] of Object.entries(deltaIdMap)) {
    // a stray is a PARTIAL todo that does not exist in original
    const notInOld = !oldTodoIdMap[todoId];
    if (notInOld) {
      mergedIdMap[todoId] = delta;
      continue;
    }

    mergedIdMap[todoId] = mergeTodo(mergedIdMap[todoId]!, delta);
  }

  return mergedIdMap;
}

//========================================================================
//
//                             Mutating Actions
//
//========================================================================
export function createTodo({
  owner = "",
  id = uuid.v4(),
  date = new Date().getTime(),
  time = null,
  duration = null,
  space,
  parent = null,
  lexidx = lexi.pack(INIT_LEX_IDX, "hex"),
  value = "",
  completed = false,
  createdDate = new Date().getTime(),
  lastEditDate = new Date().getTime(),
  meta = [],
  dueDate = null,
  collapsed = false,
  indent = 0,
  tag = null,
  deleted = false,
  notes_id = null,
  reminder = null,
}: Omit<IdentifiableTodoPartial, "id"> & Pick<Partial<Todo>, "id">): Todo {
  let todo = {
    id,
    value,
    completed: Boolean(completed),
    createdDate,
    lastEditDate,
    lexidx,
    date,
    time,
    duration,
    space: space ?? null,
    parent,
    meta,
    dueDate,
    collapsed: Boolean(collapsed),
    indent,
    owner,
    tag,
    deleted,
    notes_id,
    reminder,
  };

  return todo;
}

// for testing
export function initTodosState(mode: DisplayMode, owner = "", space = "") {
  let delta: TodoIdMap = {};

  for (let groupKey of [
    PRESET_GROUP_KEYS.BACKLOG,
    PRESET_GROUP_KEYS.TODAY,
    PRESET_GROUP_KEYS.UPCOMING,
  ]) {
    let todo = createTodo({ space, owner });

    const updateDelta = TodoGroupUtils.applyGroupKeyToTodo(
      todo,
      groupKey,
      "default",
    );
    todo = { ...todo, ...updateDelta };

    delta[todo.id] = todo;
  }

  return delta;
}

export function newTodo(
  todosState: TodoIdMap,
  date: number | null,
  parentTodoId: TodoId | null,
  insertAfterTodoId: TodoId,
  newTodoId = uuid.v4(),
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
  owner: UserId,
  space: SpaceId,
): TodoUpdateDeltaIdMap {
  let newTodo = createTodo({
    date,
    parent: parentTodoId,
    id: newTodoId,
    owner,
    space,
  });
  let deltaState = insertTodosAfter(
    todosState,
    [newTodo],
    parentTodoId,
    insertAfterTodoId,
    groupKey,
    mode,
    filterFn,
  ); //we set afterTodoId to null, as insertTodoAfter defaults to end
  return deltaState;
}

export function insertTodosBefore(
  todosState: TodoIdMap,
  insertTodos: Todo[],
  insertBeforeTodoId: TodoId,
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
): TodoUpdateDeltaIdMap {
  const orderedInsertTodos = orderedTodos(insertTodos);
  /**
   * TODO INSERTION - BEFORE
   * Simpler than insertTodosAfter, because insertTodosBefore always
   * inserts at the same indent-level as the target todo
   *
   * - Identify the todos before and after the insert range
   * - Try to distribute the todos evenly
   * - If out of range, perform a reindedx
   * - Do all this while only returning a minimal update delta
   */

  if (!orderedInsertTodos.length) return {};

  const sameLevelTodoAfterInsertPt = todosState[insertBeforeTodoId];

  if (!sameLevelTodoAfterInsertPt) {
    console.error(
      "[insertTodosBefore] Could not find todo target (after insert)",
    );
    return {};
  }

  let targetDate =
    sameLevelTodoAfterInsertPt.date == null
      ? null
      : moment(sameLevelTodoAfterInsertPt.date).startOf("day").valueOf();
  const sortedSameLevelTodos = sameLevelTodosSortedInGroup(
    todosState,
    sameLevelTodoAfterInsertPt.id,
    groupKey,
    mode,
    filterFn,
  );

  let targetTodoIndex = sortedSameLevelTodos.findIndex(
    (todo) => todo.id == insertBeforeTodoId,
  );
  if (targetTodoIndex === -1) {
    console.error("Could not find target todo", insertBeforeTodoId);
    return {};
  }

  let sameLevelTodoBeforeInsertPt = sortedSameLevelTodos[targetTodoIndex - 1];
  let afterInsertPtNumIdx = lexi.unpack(sameLevelTodoAfterInsertPt.lexidx);
  let beforeInsertPtNumIdx = sameLevelTodoBeforeInsertPt
    ? lexi.unpack(sameLevelTodoBeforeInsertPt.lexidx)
    : lexi.unpack(sameLevelTodoAfterInsertPt.lexidx) -
      (orderedInsertTodos.length + 1) /* same gap logic as below */ *
        DEFAULT_LEX_IDX_OFFSET;

  let insertIdxInterval = Math.max(
    Math.round(
      (afterInsertPtNumIdx - beforeInsertPtNumIdx) /
        /* if insert 1, gaps = 2, if insert 2, gaps = 3 */
        (orderedInsertTodos.length + 1),
    ),
    1,
  ); // at least an interval of 1

  // console.log("before", beforeInsertNumIdx, "after", afterInsertNumIdx);

  const firstInsertNumIdx = Math.max(
    beforeInsertPtNumIdx + insertIdxInterval,
    0,
  );
  const lastInsertNumIdx = Math.max(
    beforeInsertPtNumIdx + orderedInsertTodos.length * insertIdxInterval,
    0,
  );

  const needsReindex =
    beforeInsertPtNumIdx < 0 ||
    firstInsertNumIdx <= beforeInsertPtNumIdx ||
    lastInsertNumIdx >= afterInsertPtNumIdx; // out of allowable range

  // Ensure it is still indeed an insert action
  let updatedInsertTodos: Todo[] = [];
  let updateDelta: TodoUpdateDeltaIdMap = orderedInsertTodos.reduce(
    (acc, todo, i) => {
      // start after first gap
      const numIdx = Math.max(
        beforeInsertPtNumIdx + (i + 1) * insertIdxInterval,
        0,
      );
      const lexidx = lexi.pack(numIdx, "hex");

      // Only insert FULL todo if it does not exist
      const shouldInsertFullTodo = !todosState[todo.id];
      const updatedTodo = {
        ...(shouldInsertFullTodo ? todo : {}),
        ...TodoGroupUtils.applyGroupKeyToTodo(todo, groupKey, mode),
        id: todo.id,
        /**
         * IMPORTANT: reference todo should be used for reference date
         * when there exists multiple subgroups, we want to make sure
         * insert todos get created in the right subgroup.
         */
        date: targetDate,
        lexidx,
        parent: sameLevelTodoAfterInsertPt.parent, // all todos will have specified parent
      };

      acc[todo.id] = updatedTodo;
      updatedInsertTodos[i] = { ...todo, ...updatedTodo };
      return acc;
    },
    {} as TodoUpdateDeltaIdMap,
  );

  let updatedState = mergeLeftIdMaps(todosState, updateDelta);

  if (needsReindex) {
    // when attempting to sandwich newTodo, we insert with same lexidx as before
    // but insert at end of array for reindexing so that it will be sandwiched in between
    let insertIndex = sortedSameLevelTodos.findIndex(
      (t) => t.id === sameLevelTodoAfterInsertPt.id,
    );

    insertIndex = insertIndex === -1 ? 0 : insertIndex;

    const todoList = [...sortedSameLevelTodos];
    todoList.splice(insertIndex, 0, ...updatedInsertTodos);
    const reindexDelta = reindexTodos(todoList);

    updatedState = mergeLeftIdMaps(updatedState, reindexDelta);
    updateDelta = mergeLeftPartialIdMaps(updateDelta, reindexDelta);
  }

  return withReindex(
    withMatchingChildDates(updateDelta, updatedState),
    updatedState,
  );
}

/**
 * Inserts after a todo, should result in the same sub-group (i.e. date)
 */
export function insertTodosAfter(
  todoIdMap: TodoIdMap,
  insertTodos: Todo[],
  parentTodoId: TodoId | null,
  insertAfterTodoId: TodoId,
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
): TodoUpdateDeltaIdMap {
  const todosState = withoutDeleted(todoIdMap);
  const targetTodo = todosState[insertAfterTodoId];
  const targetDate =
    targetTodo?.date == null
      ? null
      : moment(targetTodo?.date).startOf("day").valueOf();
  const orderedInsertTodos = orderedTodos(insertTodos);

  if (!targetTodo) {
    console.error("[insertTodosAfter] Could not find todo");
    return {};
  }

  let sameGroupTodoList = TodoGroupUtils.todoListForGroup(
    groupKey,
    todosState,
    filterFn,
    mode,
    false,
  );

  const insertLevelTodos = sortedTodos(
    sameGroupTodoList.filter(
      (eachTodo) =>
        eachTodo.parent == parentTodoId &&
        passFilterFn(eachTodo, filterFn, todoIdMap),
    ),
  );

  let updateDelta: TodoUpdateDeltaIdMap = {};

  const isInsertAsFirstOfLevel = insertAfterTodoId == parentTodoId;
  if (isInsertAsFirstOfLevel) {
    const firstOfLevel = insertLevelTodos[0];

    if (_.isNil(firstOfLevel)) {
      // Free play, insert as is
      updateDelta = orderedInsertTodos.reduce((acc, todo, i) => {
        const shouldInsertFullTodo = !todoIdMap[todo.id];
        acc[todo.id] = {
          ...(shouldInsertFullTodo ? todo : {}),
          ...TodoGroupUtils.applyGroupKeyToTodo(todo, groupKey, mode),
          /**
           * IMPORTANT: reference todo should be used for reference date
           * when there exists multiple subgroups, we want to make sure
           * insert todos get created in the right subgroup.
           */
          date: targetDate,
          lexidx: lexi.pack(INIT_LEX_IDX + i * DEFAULT_LEX_IDX_OFFSET, "hex"),
          parent: parentTodoId, // all todos will have specified parent
        };
        return acc;
      }, {} as TodoUpdateDeltaIdMap);

      const updatedTodoState = mergeLeftIdMaps(todoIdMap, updateDelta);
      return withReindex(
        withMatchingChildDates(updateDelta, updatedTodoState),
        updatedTodoState,
      );
    } else {
      // Insert after parent, before first child
      return insertTodosBefore(
        todoIdMap,
        insertTodos,
        firstOfLevel.id,
        groupKey,
        mode,
        filterFn,
      );
    }
  }

  // From here on, we are dealing with inserting either on the
  // same level, or outer levels (same or higher level than parent of insertAfterTodoId)
  let sameLevelTodoBeforeInsertPt: Todo | undefined = undefined;
  // Iterate down until we find the parent of the target insert-level,
  // as the level of insertAfterTodoId might not be on the same level of the final insert level
  let foundInsertPoint = false;
  let insertParentTodo = parentTodoId
    ? sameGroupTodoList.find((t) => t.id === parentTodoId)
    : null; // only sameGroupTodoList has indent values

  // IMPORTANT: we must use insertParentTodo because it has indent info populated
  let targetInsertLevel = insertParentTodo ? insertParentTodo.indent + 1 : 0;

  // We need this approach in the scenario we find an "outer" level insert point
  for (let i = sameGroupTodoList.length - 1; i >= 0; i--) {
    const eachTodo = sameGroupTodoList[i]!;
    if (eachTodo.id === insertAfterTodoId) {
      foundInsertPoint = true;
    }

    if (foundInsertPoint && eachTodo.indent === targetInsertLevel) {
      sameLevelTodoBeforeInsertPt = eachTodo;
      break;
    }
  }

  if (!sameLevelTodoBeforeInsertPt)
    throw new Error(
      'Could not find insert-level-parent of specified "insert after todo"',
    );
  let sameLevelTodoAfterInsertPt: Todo | undefined;

  // now we iterate downwards till we find the after todo
  for (let i = 0; i < insertLevelTodos.length; i++) {
    const eachTodo = insertLevelTodos[i];
    if (insertLevelTodos[i - 1]?.id === sameLevelTodoBeforeInsertPt.id) {
      sameLevelTodoAfterInsertPt = eachTodo;
      break;
    }
  }

  let beforeInsertNumIdx = lexi.unpack(sameLevelTodoBeforeInsertPt.lexidx);
  if (!isDefined(sameLevelTodoAfterInsertPt)) {
    // no after todo, just insert with no index limits
    updateDelta = orderedInsertTodos.reduce((acc, todo, i) => {
      const shouldInsertFullTodo = !todoIdMap[todo.id];
      // start after first gap
      const numIdx = beforeInsertNumIdx + (i + 1) * DEFAULT_LEX_IDX_OFFSET;
      const lexidx = lexi.pack(numIdx, "hex");
      acc[todo.id] = {
        ...(shouldInsertFullTodo ? todo : {}),
        ...TodoGroupUtils.applyGroupKeyToTodo(todo, groupKey, mode),
        /**
         * IMPORTANT: reference todo should be used for reference date
         * when there exists multiple subgroups, we want to make sure
         * insert todos get created in the right subgroup.
         */
        date: targetDate,
        id: todo.id,
        lexidx,
        parent: parentTodoId, // all todos will have specified parent
      };
      return acc;
    }, {} as TodoUpdateDeltaIdMap);

    const updatedTodoState = mergeLeftIdMaps(todoIdMap, updateDelta);
    return withReindex(
      withMatchingChildDates(updateDelta, updatedTodoState),
      updatedTodoState,
    );
  } else {
    let afterInsertPtNumIdx = lexi.unpack(sameLevelTodoAfterInsertPt.lexidx);
    let beforeInsertPtNumIdx = sameLevelTodoBeforeInsertPt
      ? lexi.unpack(sameLevelTodoBeforeInsertPt.lexidx)
      : lexi.unpack(sameLevelTodoAfterInsertPt.lexidx) -
        (orderedInsertTodos.length + 1) /* same gap logic as below */ *
          DEFAULT_LEX_IDX_OFFSET;

    let insertIdxInterval = Math.max(
      Math.round(
        (afterInsertPtNumIdx - beforeInsertPtNumIdx) /
          /* if insert 1, gaps = 2, if insert 2, gaps = 3 */
          (orderedInsertTodos.length + 1),
      ),
      1,
    ); // at least an interval of 1

    const firstInsertNumIdx = Math.max(
      beforeInsertPtNumIdx + insertIdxInterval,
      0,
    );
    const lastInsertNumIdx = Math.max(
      beforeInsertPtNumIdx + orderedInsertTodos.length * insertIdxInterval,
      0,
    );

    const needsReindex =
      beforeInsertPtNumIdx < 0 ||
      firstInsertNumIdx <= beforeInsertPtNumIdx ||
      lastInsertNumIdx >= afterInsertPtNumIdx; // out of allowable range

    // Insert between, check for re-index
    let updatedInsertTodos: Todo[] = [];
    let updateDelta: TodoUpdateDeltaIdMap = orderedInsertTodos.reduce(
      (acc, todo, i) => {
        // start after first gap
        const numIdx = Math.max(
          beforeInsertPtNumIdx + (i + 1) * insertIdxInterval,
          0,
        );
        const lexidx = lexi.pack(numIdx, "hex");

        // Only insert FULL todo if it does not exist
        const shouldInsertFullTodo = !todosState[todo.id];
        const updatedTodo = {
          ...(shouldInsertFullTodo ? todo : {}),
          ...TodoGroupUtils.applyGroupKeyToTodo(todo, groupKey, mode),
          id: todo.id,
          lexidx,
          date: targetDate, // all todos will be in same date
          parent: sameLevelTodoAfterInsertPt.parent, // all todos will have specified parent
        };

        acc[todo.id] = updatedTodo;
        updatedInsertTodos[i] = { ...todo, ...updatedTodo };
        return acc;
      },
      {} as TodoUpdateDeltaIdMap,
    );

    let updatedState = mergeLeftIdMaps(todosState, updateDelta);

    const sortedSameLevelTodos = sameLevelTodosSortedInGroup(
      todoIdMap,
      sameLevelTodoAfterInsertPt.id,
      groupKey,
      mode,
      filterFn,
    );

    // Reindex if needed
    if (needsReindex) {
      let insertIndex = sortedSameLevelTodos.findIndex(
        (t) => t.id === sameLevelTodoAfterInsertPt.id,
      );

      insertIndex = insertIndex === -1 ? 0 : insertIndex;

      const todoList = [...sortedSameLevelTodos];
      todoList.splice(insertIndex, 0, ...updatedInsertTodos); // insert todos for re-indexing
      const reindexDelta = reindexTodos(todoList);

      updatedState = mergeLeftIdMaps(updatedState, reindexDelta);
      updateDelta = mergeLeftPartialIdMaps(updateDelta, reindexDelta);
    }

    return withReindex(
      withMatchingChildDates(updateDelta, updatedState),
      updatedState,
    );
  }
}

export function moveTodosToGroup(
  todosState: TodoIdMap,
  todoIds: TodoId[],
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
) {
  if (isDateBasedGroup(mode)) {
    const date = TodoGroupUtils.dateForGroupKey(groupKey, mode);
    const todos = extractTodos(todoIds, todosState);

    // moving horizontally should insert as outermost, last
    // find last todo
    let targetGroupTodoList = TodoGroupUtils.todoListForGroup(
      groupKey,
      todosState,
      filterFn,
      mode,
      false,
    );

    let lastTodoId = null;
    if (targetGroupTodoList.length !== 0) {
      lastTodoId = targetGroupTodoList[targetGroupTodoList.length - 1]!.id;
      const updateDelta = insertTodosAfter(
        todosState,
        todos,
        null,
        lastTodoId,
        groupKey,
        mode,
        filterFn,
      );

      // We prioritise the specified date over the reference todo's date
      // This is so that timezone differences will adapt to the current timezone
      // as they are moved around
      return withReindex(
        withMatchingChildDates(
          todoIds.reduce((acc, todoId) => {
            const delta = acc[todoId];
            if (delta) acc[todoId] = { ...delta, date: date };
            return acc;
          }, updateDelta),
          todosState,
        ),
        todosState,
      );
    } else {
      // if no todos in that group, insert as only ones
      const updateDelta = sortedTodos(todos).reduce((acc, todo, i) => {
        const updateDelta = TodoGroupUtils.applyGroupKeyToTodo(
          todo,
          groupKey,
          mode,
        );
        acc[todo.id] = {
          ...updateDelta,
          lexidx: lexi.pack(INIT_LEX_IDX + i * DEFAULT_LEX_IDX_OFFSET, "hex"),
          parent: null,
        };
        return acc;
      }, {} as TodoUpdateDeltaIdMap);

      return withReindex(
        withMatchingChildDates(updateDelta, todosState),
        todosState,
      );
    }
  }

  if (mode === "hashtag") {
    const todos = extractTodos(todoIds, todosState);
    const updateDelta: TodoUpdateDeltaIdMap = {};
    todos.forEach((todo) => {
      updateDelta[todo.id] = TodoGroupUtils.applyGroupKeyToTodo(
        todo,
        groupKey,
        mode,
      );
    });
    return updateDelta;
  }

  throw new Error("Unimplemented move to group");
}

export function moveTodoInDirection(
  todoId: TodoId,
  groupKey: GroupKey,
  direction: Direction,
  displayMode: DisplayMode,
  calendarModeOptions: CalendarModeOptions,
  filteredHashtags: string[],
  hashtagMetas: HashtagMeta[],
  filterFn: FilterFunction,
  todoState: TodoIdMap,
) {
  const todo = todoState[todoId];
  if (!todo) throw new Error("Todo not found");

  const todoList = TodoGroupUtils.todoListForGroup(
    groupKey,
    todoState,
    filterFn,
    displayMode,
  );

  const sameLevelTodoList = sameLevelTodosSortedInGroup(
    todoState,
    todoId,
    groupKey,
    displayMode,
    filterFn,
  );
  const isFirstOfGroup = todoList[0]?.id === todoId;
  const isOutermostLastOfGroup =
    todo.parent === null &&
    sameLevelTodoList[sameLevelTodoList.length - 1]?.id === todoId;
  const shouldJumpGroup =
    displayMode === "calendar" &&
    ((direction === "up" && isFirstOfGroup) ||
      (direction === "down" && isOutermostLastOfGroup));

  if (direction === "left" || direction === "right" || shouldJumpGroup) {
    const targetGroupKey = TodoUtils.groupInDirection(
      todoState,
      groupKey,
      direction,
      displayMode,
      filteredHashtags,
      hashtagMetas,
      calendarModeOptions,
    );
    return {
      updatedGroupKey: targetGroupKey,
      delta: TodoUtils.moveTodosToGroup(
        todoState,
        [todoId],
        targetGroupKey,
        displayMode,
        filterFn,
      ),
    };
  }

  // Move vertically within group
  return {
    updatedGroupKey: groupKey,
    delta: TodoUtils.moveTodoVertical(
      todoState,
      todoId,
      groupKey,
      direction === "up",
      displayMode,
      filterFn,
    ),
  };
}

export function updateChildrenDateToMatch(
  todoIdMap: TodoIdMap,
  parentTodoId: TodoId,
  date: number | null,
): TodoUpdateDeltaIdMap {
  let deltaState: TodoUpdateDeltaIdMap = {};
  //1. get todo and all its children
  let children = deepChildrenOfTodo(todoIdMap, parentTodoId);

  //2. update todo and children's date
  let targetDate = date == null ? null : moment(date).startOf("day").valueOf();

  children.forEach((childTodo) => {
    deltaState[childTodo.id] = { id: childTodo.id, date: targetDate };
  });

  return deltaState;
}

function withMatchingChildDates(
  deltaIdMap: TodoUpdateDeltaIdMap,
  todoIdMap: TodoIdMap,
  parentChildMapCache = parentChildMap(Object.values(todoIdMap)),
): TodoUpdateDeltaIdMap {
  let updatedDeltaIdMap = _.cloneDeep(deltaIdMap);
  // for all updates, if there has been a change to the date field, update children to also have date field
  const updateTodos = Object.values(deltaIdMap);
  const updateTodoParents = Object.values(deltaIdMap)
    .map((t) => todoIdMap[todoIdMap[t.id]?.parent ?? ""])
    .filter(isFullTodo);

  const allParentTodos = new Set([...updateTodos, ...updateTodoParents]);

  for (let parent of allParentTodos) {
    if (parent.date === undefined) continue;
    const deepChildren = deepChildrenOfTodo(
      todoIdMap,
      parent.id,
      parentChildMapCache,
    );
    const childUpdateDelta: TodoUpdateDeltaIdMap = {};
    for (let child of deepChildren) {
      if (child.date !== parent.date) {
        childUpdateDelta[child.id] = { id: child.id, date: parent.date };
      }
    }

    updatedDeltaIdMap = mergeLeftPartialIdMaps(
      updatedDeltaIdMap,
      childUpdateDelta,
    );
  }

  return updatedDeltaIdMap;
}

function withReindex(deltaIdMap: TodoUpdateDeltaIdMap, todoIdMap: TodoIdMap) {
  let updatedDeltaIdMap = _.cloneDeep(deltaIdMap);
  const reindexDelta = reindexIfNeeded(todoIdMap, "default");
  updatedDeltaIdMap = mergeLeftPartialIdMaps(updatedDeltaIdMap, reindexDelta);
  return updatedDeltaIdMap;
}

export function moveTodoVertical(
  todosState: TodoIdMap,
  todoId: TodoId,
  groupKey: GroupKey,
  isUp: boolean,
  mode: DisplayMode,
  filterFn: FilterFunction,
): TodoUpdateDeltaIdMap {
  let todo = _.cloneDeep(todosState[todoId]);

  if (_.isNil(todo)) {
    console.warn("Attempted to vertical move todo that does not exist");
    return {};
  }

  let sameLevelTodos = sameLevelTodosSortedInGroup(
    todosState,
    todoId,
    groupKey,
    mode,
    filterFn,
  );

  if (isUp) {
    if (!sameLevelTodos[0])
      throw new Error("Unexpected empty same level todos");
    //a. if is first in list, make last
    let isFirstOfLevel = sameLevelTodos[0].id == todo.id;
    if (isFirstOfLevel) {
      if (_.isNil(todo.parent)) {
        //if is first, make last
        let lastTodoInSameLevel = sameLevelTodos[sameLevelTodos.length - 1]!;
        let deltaState = insertTodosAfter(
          todosState,
          [todo],
          todo.parent,
          lastTodoInSameLevel.id,
          groupKey,
          mode,
          filterFn,
        );
        return deltaState;
      } else {
        //this todo has a parent. we move it to be above the parent
        let parentTodoId = todo.parent as TodoId;
        let parentTodo = todosState[parentTodoId];
        let deltaState = insertTodosBefore(
          todosState,
          [todo],
          parentTodo!.id,
          groupKey,
          mode,
          filterFn,
        );
        return deltaState;
      }
    } else {
      for (let i = 0; i < sameLevelTodos.length; i++) {
        let isTargetTodo = sameLevelTodos[i]!.id == todo.id;
        if (isTargetTodo) {
          let previousTodo = sameLevelTodos[i - 1]!; // assert there is a previous, since isFirstOfLevel = false
          let isSameGroupDateAsTargetTodo = isSameDay(
            previousTodo.date,
            todo.date,
          );
          if (!isSameGroupDateAsTargetTodo) {
            let deltaState = insertTodosAfter(
              todosState,
              [todo],
              todo.parent,
              previousTodo.id,
              groupKey,
              mode,
              filterFn,
            );
            return deltaState;
          }

          let deltaState = insertTodosBefore(
            todosState,
            [todo],
            previousTodo.id,
            groupKey,
            mode,
            filterFn,
          );
          return deltaState;
        }
      }

      return {};
    }
  } else {
    //if is last in list, make first
    let isLastOfLevel =
      sameLevelTodos[sameLevelTodos.length - 1]!.id == todo.id;
    if (isLastOfLevel) {
      if (_.isNil(todo.parent)) {
        //if last, make first
        if (sameLevelTodos.length > 0) {
          let firstTodoOldId = sameLevelTodos[0]!.id;
          return insertTodosBefore(
            todosState,
            [todo],
            firstTodoOldId,
            groupKey,
            mode,
            filterFn,
          );
        } else {
          return {}; // no need to move, as it is only todo
        }
      } else {
        //has parent. move outside of parent
        //this todo has a parent. we move it to be after the parent
        let parentTodoId = todo.parent as TodoId;
        let parentTodo = todosState[parentTodoId];
        if (!parentTodo) {
          return {};
        }
        let deltaState = insertTodosAfter(
          todosState,
          [todo],
          parentTodo.parent,
          parentTodoId,
          groupKey,
          mode,
          filterFn,
        );
        return deltaState;
      }
    } else {
      for (let i = 0; i < sameLevelTodos.length; i++) {
        let isTargetTodo = sameLevelTodos[i]!.id == todo.id;
        if (isTargetTodo) {
          // isLastOfLevel = false, so there is a next
          let nextTodo = sameLevelTodos[i + 1]!;
          let nextLexIdx = nextTodo.lexidx;

          // if is same date within group, swap index only
          let isSameGroupDateAsTargetTodo = isSameDay(nextTodo.date, todo.date);
          if (isSameGroupDateAsTargetTodo) {
            nextTodo.lexidx = todo.lexidx;
            todo.lexidx = nextLexIdx;
            // swap index
            let deltaState = {
              [nextTodo.id]: nextTodo,
              [todo.id]: todo,
            };
            return deltaState;
          } else {
            // we need to change this todo's date
            // and insert before todo
            let deltaState = insertTodosBefore(
              todosState,
              [todo],
              nextTodo.id,
              groupKey,
              mode,
              filterFn,
            );
            return deltaState;
          }
        }
      }
      return {};
    }
  }
}

const MAX_INDENT_LEVEL = 3;
export function indentTodo(
  todosState: TodoIdMap,
  todoId: TodoId,
  offset: number,
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
): TodoUpdateDeltaIdMap {
  //1. get previous parent of same level
  let targetTodo = todosState[todoId];
  if (_.isNil(targetTodo)) {
    throw new Error("todo does not exist");
  }

  //indent ->
  if (offset == 1) {
    //2. make last child of that todo
    let sameGroupTodoList = TodoGroupUtils.todoListForGroup(
      groupKey,
      todosState,
      filterFn,
      mode,
      false,
    );
    let sortedTodosSameLevel = sortedTodos(
      sameGroupTodoList.filter(
        (eachTodo) =>
          eachTodo.parent == targetTodo!.parent &&
          passFilterFn(eachTodo, filterFn, todosState),
      ),
    );
    for (let i = 0; i < sortedTodosSameLevel.length; i++) {
      let isTargetTodo = sortedTodosSameLevel[i]!.id == todoId;
      if (isTargetTodo) {
        //has no previous, so cannot indent
        if (i - 1 < 0) {
          throw "no_previous";
        }

        let previousTodo = sortedTodosSameLevel[i - 1]!;
        const parentChildMap = TodoUtils.parentChildMap(
          Object.values(todosState),
        );

        // DFS to find deep child depth
        const dfs = (todo: Todo, depth: number): number => {
          const children = parentChildMap[todo.id];
          if (!children) {
            return depth;
          }
          let maxDepth = depth;
          for (let child of children) {
            const childDepth = dfs(child, depth + 1);
            maxDepth = Math.max(maxDepth, childDepth);
          }
          return maxDepth;
        };

        const deepChildDepth = dfs(targetTodo, 0);
        if (previousTodo.indent + deepChildDepth >= MAX_INDENT_LEVEL) {
          throw "max_indent";
        }
        let parentTodoId = previousTodo.id;

        let sortedChildrenOfPreviousTodo = sortedTodos(
          Object.values(todosState).filter(
            (eachTodo) =>
              eachTodo!.parent == previousTodo.id &&
              passFilterFn(eachTodo, filterFn, todosState),
          ) as Todo[],
        );
        let lastChildTodo =
          sortedChildrenOfPreviousTodo[sortedChildrenOfPreviousTodo.length - 1];
        let insertAfterTodoId = lastChildTodo?.id || parentTodoId;
        let deltaState = insertTodosAfter(
          todosState,
          [targetTodo],
          parentTodoId,
          insertAfterTodoId,
          groupKey,
          mode,
          filterFn,
        );
        return deltaState;
      }
    }

    return {};
  } else {
    //indent <-
    //1.set parent same as parent's parent
    //2.insert after parent

    //1.
    let parentTodo = targetTodo.parent ? todosState[targetTodo.parent] : null;
    let parentTodoId = (parentTodo && parentTodo.id) || null;

    if (!parentTodoId) {
      throw "no_parent";
    }

    if (!parentTodoId) return {};

    let parentParentTodoId = (parentTodo && parentTodo.parent) || null;
    let deltaState = insertTodosAfter(
      todosState,
      [targetTodo],
      parentParentTodoId,
      parentTodoId,
      groupKey,
      mode,
      filterFn,
    );
    return deltaState;
  }
}

export function hardDeleteTodo(
  todoState: TodoIdMap,
  todoId: TodoId,
): {
  todoIdMap: TodoIdMap;
  deleted: Todo[];
  updated: Todo[];
} {
  const newState = _.cloneDeep(todoState);
  const deletedTodo = newState[todoId];
  if (!deletedTodo) {
    return {
      todoIdMap: newState,
      deleted: [],
      updated: [],
    };
  }
  delete newState[todoId];

  const children = childrenOfTodo(todoState, todoId);
  const updated = children.map((todo) => {
    // update state, to unparent
    const updatedChild = { ...todo, parent: null };
    newState[todo.id] = updatedChild;
    return updatedChild;
  });

  return {
    todoIdMap: newState,
    deleted: [deletedTodo],
    updated,
  };
}

export function softDeleteTodo(
  todoState: TodoIdMap,
  todoId: TodoId,
): {
  updatedIdMap: TodoUpdateDeltaIdMap;
} {
  const newState = _.cloneDeep(todoState);
  const deletedTodo = newState[todoId];
  if (!deletedTodo) {
    return {
      updatedIdMap: {},
    };
  }
  const deletedTodoMap = { [todoId]: { id: todoId, deleted: true } };
  const children = childrenOfTodo(todoState, todoId);
  const updatedTodoMap = children.reduce((idMap, cur) => {
    // transfer ownership to parent's parent
    return { ...idMap, [cur.id]: { id: cur.id, parent: deletedTodo.parent } };
  }, {} as TodoUpdateDeltaIdMap);
  return {
    updatedIdMap: { ...updatedTodoMap, ...deletedTodoMap },
  };
}

export function softDeleteTodoDeep(
  todoState: TodoIdMap,
  todoId: TodoId,
): {
  updatedIdMap: TodoUpdateDeltaIdMap;
} {
  const newState = _.cloneDeep(todoState);

  const parent = newState[todoId];
  if (!parent) {
    return {
      updatedIdMap: {},
    };
  }

  const children = deepChildrenOfTodo(newState, todoId);
  const todosToDelete = [parent, ...children];
  const deletedTodoMap = todosToDelete.reduce((idMap, cur) => {
    return { ...idMap, [cur.id]: { id: cur.id, deleted: true } };
  }, {} as TodoUpdateDeltaIdMap);

  return {
    updatedIdMap: { ...deletedTodoMap },
  };
}

/**
 * deletes deep children too
 */
export function hardDeleteTodoDeep(
  todoState: TodoIdMap,
  todoId: TodoId,
): {
  todoIdMap: TodoIdMap;
  deleted: Todo[];
} {
  const newState = _.cloneDeep(todoState);

  const children = deepChildrenOfTodo(todoState, todoId);
  const deletedTodos: Todo[] = [];
  deletedTodos.push(newState[todoId]!);
  delete newState[todoId];

  children.forEach((todo) => {
    deletedTodos.push(newState[todo.id]!);
    delete newState[todo.id];
  });

  return {
    todoIdMap: newState,
    deleted: deletedTodos,
  };
}

export function rolloverYstd(todoIdMap: TodoIdMap): TodoUpdateDeltaIdMap {
  const displayMode: DisplayMode = "default";
  let allTodosList = Object.values(withoutDeleted(todoIdMap));
  const pcMap = parentChildMap(allTodosList);

  let yesterdayOutermostTodos = TodoUtils.sortedTodos(
    allTodosList.filter((todo) => {
      if (todo.parent) return false; // only get outermost todos
      if (isBranchComplete(todo.id, todoIdMap, pcMap)) return false; // don't rollover completed branches

      const isYesterday = moment(todo.date).isBetween(
        moment().subtract(1, "day").startOf("day"),
        moment().subtract(1, "day").endOf("day"),
        undefined, // default precision to ms
        "[]", // inclusive
      );

      return isYesterday;
    }) as Todo[],
  );

  return moveTodosToGroup(
    todoIdMap,
    yesterdayOutermostTodos.map((t) => t.id),
    PRESET_GROUP_KEYS.TODAY,
    "default", // Force default view so we can be sure there is a "today" column
    nullFilterFn,
  );
}
export function rolloverBacklog(todoIdMap: TodoIdMap): TodoUpdateDeltaIdMap {
  const displayMode: DisplayMode = "default";
  let allTodosList = Object.values(withoutDeleted(todoIdMap));
  const pcMap = parentChildMap(allTodosList);

  let backlogOutermostTodos = TodoUtils.sortedTodos(
    allTodosList.filter((todo) => {
      if (todo.parent) return false; // only get outermost todos
      if (isBranchComplete(todo.id, todoIdMap, pcMap)) return false; // don't rollover completed branches

      const isBacklog = moment(todo.date).isBefore(moment().startOf("day"));

      return isBacklog;
    }) as Todo[],
  );
  return moveTodosToGroup(
    todoIdMap,
    backlogOutermostTodos.map((t) => t.id),
    PRESET_GROUP_KEYS.TODAY,
    "default",
    nullFilterFn,
  );
}
export function backlogToSomeday(
  todoIdMap: TodoIdMap,
  mode: DisplayMode,
  filterFn: FilterFunction,
): TodoUpdateDeltaIdMap {
  let deltaTodoIdMap: TodoUpdateDeltaIdMap = {};
  let allTodosList = Object.values(withoutDeleted(todoIdMap));
  let outermostTodos = allTodosList.filter((todo) => todo!.parent == null);
  let backlogTodos = TodoUtils.sortedTodos(
    outermostTodos.filter((todo) => {
      return moment(todo.date).isBefore(moment().startOf("day"));
    }) as Todo[],
  );

  for (let todo of backlogTodos) {
    let deltaState = moveTodosToGroup(
      todoIdMap,
      [todo.id],
      "someday",
      "default", // Force default view so we can be sure there is a "today" column
      filterFn,
    );
    // accumulate updates
    deltaTodoIdMap = { ...deltaTodoIdMap, ...deltaState };
  }

  return deltaTodoIdMap;
}

export function reindexIfNeeded(
  todoState: TodoIdMap,
  mode: DisplayMode,
): TodoUpdateDeltaIdMap {
  // reindexing should happen for unfiltered set of todos
  let groups = TodoGroupUtils.todoListByGroup(todoState, mode, nullFilterFn);
  let updateDeltaIdMap: TodoUpdateDeltaIdMap = {};

  function hasDuplicateLexIdx(list: Todo[]) {
    let existingLexIdx: Record<string, boolean> = {};
    for (let todo of list) {
      if (existingLexIdx[todo.lexidx]) {
        return true;
      }
      existingLexIdx[todo.lexidx] = true;
    }
    return false;
  }

  for (let [groupKey, list] of Object.entries(groups)) {
    // outermost
    const outermostTodos = list.filter((todo) => todo.parent == null);
    if (hasDuplicateLexIdx(outermostTodos)) {
      updateDeltaIdMap = {
        ...updateDeltaIdMap,
        ...reindexTodos(outermostTodos),
      };
    }

    // children
    const pcMap = parentChildMap(list);
    for (let outermostTodo of outermostTodos) {
      const children = pcMap[outermostTodo.id] ?? [];
      if (hasDuplicateLexIdx(children)) {
        updateDeltaIdMap = { ...updateDeltaIdMap, ...reindexTodos(children) };
      }
    }
  }

  return updateDeltaIdMap;
}

export function migrateTodos(
  _todoIds: string[],
  todoIdMap: TodoIdMap,
  toSpaceId: SpaceId,
) {
  // 1. all children should follow parents
  // 2. check if todo has parent, and parent is in todos to migrate
  //  - if not, de-parent
  // 3. return a list of todo deltas with updated spaceIds and parentIds

  // rarely we allow updating of space
  // this is the one place we allow it
  const deltaUpdateIdMap: {
    [id: TodoId]: Partial<Todo> & Pick<Todo, "space" | "id" | "owner">;
  } = {};

  const todosToMigrate: Todo[] = [];
  const droppedIds: string[] = [];
  for (let todoId of _todoIds) {
    const todo = todoIdMap[todoId];
    if (!todo) {
      droppedIds.push(todoId);
      continue;
    }
    todosToMigrate.push(todo);
  }

  const todoIds = todosToMigrate.map((t) => t.id);
  const todoIdSet = new Set(todoIds);
  const parentChildMap = TodoUtils.parentChildMap(Object.values(todoIdMap));

  // 1. all children must also be migrated
  for (let todo of todosToMigrate) {
    const deepChildren = TodoUtils.deepChildrenOfTodo(
      todoIdMap,
      todo.id,
      parentChildMap,
    );
    for (let child of deepChildren) {
      todoIdSet.add(child.id);
    }
  }

  const finalTodoIds = Array.from(todoIdSet);
  //

  const updatedTodoIdMap: TodoIdMap = {};

  for (let todoId of finalTodoIds) {
    const todo = todoIdMap[todoId]; // all todos that should be dropped are dropped. All children are derived
    if (!todo) {
      throw new Error(`todo with id does not exist: ${todoId}`);
    }

    const delta = {
      id: todo.id,
      owner: todo.owner,
      space: toSpaceId,
    };

    deltaUpdateIdMap[todoId] = delta;
    updatedTodoIdMap[todoId] = { ...todo, ...delta };

    if (todo.parent && !todoIdSet.has(todo.parent)) {
      // IMPORTANT: parent is missing from movement, de-parent the todo
      deltaUpdateIdMap[todo.id]!.parent = null;
    }
  }

  return {
    droppedIds,
    deltaUpdateIdMap,
    updatedTodoIdMap,
  };
}
//========================================================================
//
//                             GETTER FUNCTIONS
//
//========================================================================
export type TodoListHeader = {
  isHeader: boolean;
  title: string;
  date: number | null;
  count: number;
  flag: boolean;
};

export function isTodoListHeader(
  item: Todo | TodoListHeader,
): item is TodoListHeader {
  return (item as TodoListHeader).isHeader;
}

export function withHeaders(todos: Todo[], displayMode: DisplayMode) {
  if (displayMode === "calendar") return todos; // No need for headers since every list should be a single day

  const todosWithHeaders: (Todo | TodoListHeader)[] = [];

  // reset for next, undefined because someday is null
  let lastTodoDate: moment.Moment | null | undefined = undefined;

  let lastHeader: TodoListHeader | null = null;
  todos.forEach((todo, i) => {
    // check if need to add a header
    const title = TodoGroupUtils.subGroupTitle(todo.date, displayMode);
    const date = todo.date ? moment(todo.date).startOf("day") : null;

    if (!TodoUtils.isSameDay(date, lastTodoDate)) {
      const header = {
        isHeader: true,
        title,
        date: date?.valueOf() ?? null,
        count: 0,
        flag: false,
      };
      todosWithHeaders.push(header);
      lastHeader = header;
    }

    if (lastHeader) {
      lastHeader.count++;

      const isFlaggable = TodoUtils.isFlaggable(todo);
      lastHeader.flag = lastHeader.flag || isFlaggable;
    }

    lastTodoDate = date;

    todosWithHeaders.push(todo);
  });

  return todosWithHeaders;
}

export function isFlaggable(todo: Todo) {
  // a todo is worth flagging if
  // 1. is important
  // 2. is urgent
  // 3. is due in 3 days
  return Boolean(
    todo.tag === "important" ||
    todo.tag === "urgent" ||
    (todo.dueDate &&
      moment(todo.dueDate).isBetween(
        moment().startOf("day"),
        moment().add(3, "days").endOf("day"),
      )),
  );
}

export function collapsedParentOfTodo(todoState: TodoIdMap, todo: Todo) {
  return null;
}

export function previousTodo(todoState: TodoIdMap, todoId: TodoId) {
  return null;
}

export function nextTodo(
  todoState: TodoIdMap,
  todoId: TodoId,
  groupKey: GroupKey,
  displayMode: DisplayMode,
  filterFn: FilterFunction,
) {
  const todo = todoState[todoId];
  if (!todo) return null;

  const todayTodos = TodoGroupUtils.todoListForGroup(
    groupKey,
    todoState,
    filterFn,
    displayMode,
    false,
  );
  let curTodoIndex = todayTodos.findIndex((t) => t.id === todoId);
  let nextTodo = todayTodos[curTodoIndex + 1];
  while (curTodoIndex < todayTodos.length) {
    if (nextTodo) return nextTodo;
    curTodoIndex++;
    nextTodo = todayTodos[curTodoIndex];
  }

  return null;
}

// export function todoInDirection(
//   todoIdMap: TodoIdMap,
//   todoId: TodoId,
//   groupKey: GroupKey,
//   direction: Direction,
//   mode: DisplayMode,
//   filterFn: FilterFunction,
//   filteredHashtags: string[]
// ) {
//   let sourceTodo = todoIdMap[todoId];

//   if (!sourceTodo) {
//     console.log(`Could not find todo for id: ${todoId}`);
//     return;
//   }

//   let dir = {
//     right: [1, null],
//     left: [-1, null],
//     up: [null, -1],
//     down: [null, 1],
//   };

//   let horOffset = dir[direction][0];
//   let vertOffset = dir[direction][1];

//   let todosDisplay = TodoGroupUtils.todoListByGroup(todoIdMap, mode, filterFn);
//   let todosOnSourceDate = todosDisplay[groupKey] || [];
//   let todoIndex = todosOnSourceDate.findIndex(
//     (eachTodo) => eachTodo.id == sourceTodo!.id
//   );
//   if (todoIndex == -1)
//     todosOnSourceDate.findIndex(
//       (eachTodo) => eachTodo.id == sourceTodo!.parent
//     ); //try to find parent index if collapsed
//   if (todoIndex == -1) todoIndex = 0;

//   if (horOffset) {
//     //1. get list of todos for the targeted day
//     let targetGroup = groupInDirection(
//       todoIdMap,
//       groupKey,
//       direction,
//       mode,
//       filteredHashtags
//     );
//     let todosInSameGroup = todosDisplay[targetGroup];

//     if (!todosInSameGroup) {
//       // no todos exist yet in this group
//       // either include placeholders in idMap or don't
//       // include:
//       // has to be created upon load, might result in double empties
//       // don't include:
//       // has to access placeholders from a separate value
//       console.error("no todos in target group");
//       return null;
//     }
//     //2. select item in new date based on source index
//     let index = todoIndex;
//     index = Math.min(index, todosInSameGroup.length - 1);
//     return (todosInSameGroup && todosInSameGroup[index]) || null;
//   }

//   if (vertOffset) {
//     let targetIndex = mod(todoIndex + vertOffset, todosOnSourceDate.length);
//     return todosOnSourceDate[targetIndex] || null;
//   }
// }

function getCompletedTodosForDate(
  date: Date | number | null,
  fullTodoIdMap: TodoIdMap,
  displayMode: DisplayMode,
  filterFn: FilterFunction,
) {
  let completedTodoIdMap: TodoIdMap = {};
  const todayTodos = Object.values(fullTodoIdMap).filter((todo) =>
    TodoUtils.isSameDay(todo.date, date),
  );
  for (let todo of todayTodos) {
    if (TodoUtils.isBranchComplete(todo.id, fullTodoIdMap)) {
      completedTodoIdMap[todo.id] = todo;
    }
  }
  const groupKey = TodoGroupUtils.groupKeyForDate(date, displayMode);
  if (!groupKey)
    throw new Error(
      `Unexpected empty group key, invalid displayMode ${displayMode}`,
    );
  const completedTodos = TodoGroupUtils.todoListForGroup(
    groupKey,
    completedTodoIdMap,
    filterFn,
    displayMode,
    true,
  );

  return completedTodos;
}

function groupKeysOrdered(
  todoIdMap: TodoIdMap,
  mode: DisplayMode,
  filteredHashtags: string[],
  hashtagMetas: HashtagMeta[],
  calendarModeOptions: CalendarModeOptions | null,
): GroupKey[] {
  switch (mode) {
    case "zen": {
      return ["today"];
    }
    case "default": {
      return [
        PRESET_GROUP_KEYS.BACKLOG,
        PRESET_GROUP_KEYS.TODAY,
        PRESET_GROUP_KEYS.UPCOMING,
      ];
    }

    case "week": {
      let weekDays = [];
      for (let i = 1; i < WEEK_FORECAST_DAYS; i++) {
        const groupKey = TodoGroupUtils.groupKeyForDate(
          moment().add(i, "days").startOf("day"),
          "week",
        );
        if (!groupKey)
          throw new Error(`Unexpected invalid group key ${groupKey}`);
        weekDays.push(groupKey);
      }
      return [
        PRESET_GROUP_KEYS.BACKLOG,
        PRESET_GROUP_KEYS.TODAY,
        ...weekDays,
        PRESET_GROUP_KEYS.SOMEDAY,
      ];
    }

    case "hashtag": {
      let orderedHashtags = hashtagsOrdered(todoIdMap, hashtagMetas);
      // If filtered hashtags are provided, only return those that match
      if (filteredHashtags.length) {
        orderedHashtags = orderedHashtags.filter((hashtag) =>
          filteredHashtags.includes(hashtag),
        );
      }
      return orderedHashtags;
    }

    case "calendar": {
      if (!calendarModeOptions)
        throw new Error(
          "Calendar mode options are required when displayMode is calendar",
        );

      const groupKeyGrid = TodoGroupUtils.getCalendarGroupKeyGrid(
        calendarModeOptions.referenceDate,
        calendarModeOptions.firstDayInWeek,
      );
      const groupKeys = groupKeyGrid.flatMap((row) => row);

      return groupKeys;
    }
  }
}

function groupInDirection(
  todoIdMap: TodoIdMap,
  srcGroupKey: GroupKey,
  direction: Direction,
  mode: DisplayMode,
  filteredHashtags: string[],
  hashtagMetas: HashtagMeta[],
  calendarModeOptions: CalendarModeOptions,
): GroupKey {
  const groupKeys = groupKeysOrdered(
    todoIdMap,
    mode,
    filteredHashtags,
    hashtagMetas,
    calendarModeOptions,
  );

  switch (mode) {
    case "default": {
      if (direction === "up" || direction === "down") {
        return srcGroupKey;
      }

      let curIndex = groupKeys.indexOf(srcGroupKey);

      if (curIndex === -1) {
        // any other date, assume in future
        curIndex = groupKeys.indexOf(PRESET_GROUP_KEYS.UPCOMING);
      }

      let groupIndex = mod(
        (curIndex += direction === "left" ? -1 : 1),
        groupKeys.length,
      );
      return groupKeys[groupIndex]!;
    }

    case "week": {
      if (direction === "up" || direction === "down") {
        return srcGroupKey;
      }

      // next 5 days

      let curIndex = groupKeys.indexOf(srcGroupKey);

      if (curIndex === -1) {
        // any other date, assume in future
        curIndex = groupKeys.indexOf(PRESET_GROUP_KEYS.SOMEDAY);
      }

      let groupIndex = mod(
        (curIndex += direction === "left" ? -1 : 1),
        groupKeys.length,
      );
      return groupKeys[groupIndex]!;
    }

    case "zen": {
      if (direction === "up" || direction === "down") {
        return srcGroupKey;
      }

      return groupKeys[0]!;
    }

    case "hashtag": {
      if (direction === "up" || direction === "down") {
        return srcGroupKey;
      }

      let curIndex = groupKeys.indexOf(srcGroupKey);

      if (curIndex === -1) {
        throw new Error(
          `Could not find group in direction src: ${srcGroupKey} dir: ${direction}`,
        );
      }

      let groupIndex = mod(
        (curIndex += direction === "left" ? -1 : 1),
        groupKeys.length,
      );
      return groupKeys[groupIndex]!;
    }

    case "calendar": {
      if (!calendarModeOptions)
        throw new Error(
          "Calendar mode options required when displayMode is calendar",
        );
      const groupKeyGrid = TodoGroupUtils.getCalendarGroupKeyGrid(
        calendarModeOptions.referenceDate,
        calendarModeOptions.firstDayInWeek,
      );
      let x;
      let y;
      if (!groupKeyGrid[0]) throw new Error("Unexpected empty grid");

      const gridWidth = groupKeyGrid[0].length;
      const gridHeight = groupKeyGrid.length;
      for (let i = 0; i < gridHeight; i++) {
        for (let j = 0; j < gridWidth; j++) {
          if (groupKeyGrid[i]![j] === srcGroupKey) {
            x = j;
            y = i;
            break;
          }
        }
      }

      const defaultGroupKey = groupKeyGrid[0]?.[0]; // default to first group
      if (!defaultGroupKey) throw new Error("No default group key found");

      if (x === undefined || y === undefined) {
        console.warn(
          `Could not find coordinates for groupKey ${srcGroupKey}`,
          groupKeyGrid,
        );
        return defaultGroupKey;
      }

      if (direction === "left") x = mod(x - 1, gridWidth);
      if (direction === "right") x = mod(x + 1, gridWidth);
      if (direction === "up") y = mod(y - 1, gridHeight);
      if (direction === "down") y = mod(y + 1, gridHeight);

      const groupKey = groupKeyGrid[y]?.[x];
      if (!groupKey) {
        console.warn(`Could not find groupKey at coordinates (${x}, ${y})`);
        return defaultGroupKey;
      }

      return groupKey;
    }
  }
}

export function todosForGroup(
  todoIdMap: TodoIdMap,
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
): Todo[] {
  const todos =
    TodoGroupUtils.todoListByGroup(withoutDeleted(todoIdMap), mode, filterFn)[
      groupKey
    ] ?? [];
  return todos;
}

/**
 * Returns all todos with due dates, sorted by due date within each
 * day.
 */
export function dueTodosByDay(todosState: TodoIdMap, mode: DisplayMode) {
  if (!isDateBasedGroup(mode)) return {};

  let dueTodosByDay: TodoListByGroupKey = {};

  for (let todo of Object.values<Todo>(todosState)) {
    if (!todo.completed && todo.dueDate) {
      const groupKey = TodoGroupUtils.groupKeyForDate(todo.dueDate, mode);
      if (!groupKey) {
        console.log(`No group key: ${todo.dueDate} ${mode}`);
        continue;
      }
      dueTodosByDay[groupKey] ??= [];
      dueTodosByDay[groupKey].push(todo);
    }
  }

  // Sort by date
  for (const [groupKey, todos] of Object.entries(dueTodosByDay)) {
    dueTodosByDay[groupKey] = todos.sort((a, b) => {
      if (!a.dueDate || !b.dueDate) return 0;
      return a.dueDate - b.dueDate;
    });
  }

  return dueTodosByDay;
}

export function sameLevelTodosSortedInGroup(
  todoIdMap: TodoIdMap,
  todoId: TodoId,
  groupKey: GroupKey,
  mode: DisplayMode,
  filterFn: FilterFunction,
) {
  let todo = todoIdMap[todoId];

  if (!todo) {
    return [];
  }

  // already sorted within toodListByGrou
  const todos = TodoGroupUtils.todoListForGroup(
    groupKey,
    todoIdMap,
    filterFn,
    mode,
    false,
  );
  return todos.filter((eachTodo) => eachTodo.parent == todo!.parent);
}

function parentChildMap(todoList: Todo[]) {
  let childrenMap: { [parentTodoId: TodoId]: Todo[] } = {};
  //map children to parents
  for (let todo of todoList) {
    if (!_.isNil(todo.parent)) {
      let parentTodoId = todo.parent as TodoId;
      if (_.isNil(childrenMap[parentTodoId])) {
        childrenMap[parentTodoId] = [todo];
      } else {
        childrenMap[parentTodoId]!.push(todo);
      }
    }
  }

  return childrenMap;
}

/**
 * Returns root parent, even if todo is deleted
 */
function rootOfTodo(todoIdMap: TodoIdMap, todoId: TodoId): Todo | null {
  let todo = todoIdMap[todoId];
  if (!todo) {
    console.error(`rootOfTodo: Todo does not exist for id: ${todoId}`);
    return null;
  }

  let parent = todo.parent;
  if (!parent) {
    return todo;
  }

  let parentExists =
    parent && todoIdMap[parent] && todoIdMap[parent]!.deleted == false;
  if (!parentExists) {
    return todo;
  }

  return rootOfTodo(todoIdMap, parent);
}

export function childrenOfTodo(
  todoIdMap: TodoIdMap,
  todoId: TodoId,
  sorted?: boolean,
): Todo[] {
  let targetTodo = todoIdMap[todoId];
  if (!targetTodo) {
    console.error(`childrenOfTodo: Todo does not exist for id: ${todoId}`);
    return [];
  }

  const children = (Object.values(withoutDeleted(todoIdMap)) as Todo[]).filter(
    (todo) => todo.parent && todo.parent == todoId,
  );

  if (!sorted) {
    return children;
  }

  return TodoUtils.sortedTodos(children);
}

export function visibleChildrenOfTodo(todoId: TodoId, todoList: Todo[]) {
  const children: Todo[] = [];

  let lastIndent = 0;
  let targetTodo: Todo | null = null;
  for (let todo of todoList) {
    lastIndent = todo.indent;

    if (todo.id === todoId) {
      targetTodo = todo;
      continue;
    }

    if (targetTodo) {
      if (todo.indent <= targetTodo.indent) break;
      children.push(todo);
    }
  }
  return children;
}

export function deepChildrenOfTodo(
  todosState: TodoIdMap,
  todoId: TodoId,
  parentChildMapCache: ReturnType<typeof parentChildMap> = parentChildMap(
    Object.values(todosState) as Todo[],
  ),
  includeSelf = false,
) {
  let targetTodo = todosState[todoId];
  let deepChildren = [];
  if (!targetTodo) {
    console.error(`deepChildrenOfTodo: Todo does not exist for id: ${todoId}`);
    return [];
  }

  let queue = [targetTodo];
  while (queue.length != 0) {
    let todo = queue.shift() as Todo;

    if (todo.id != targetTodo.id) {
      deepChildren.push(todo);
    }

    let children = sortedTodos(parentChildMapCache[todo.id] || []);
    queue = [...children, ...queue]; //insert children at front - dfs
  }

  if (includeSelf) {
    deepChildren.unshift(targetTodo);
  }

  return deepChildren;
}

// export function todosByDay(todosState) {
//     let todosByDay = {};

//     for(let i=0; i<7; i++) {
//         todosByDay[_keyForDate(moment())] = [];
//     }

//     todosByDay['someday'] = []

//     for(let todo of Object.values(todosState)) {
//         let dayKey = todosByDay[_keyForDate(todo.date)];
//         if(todosByDay[dayKey]) {
//             todosByDay[dayKey].push(todo);
//         }
//         //if no day key, means the day is not in the next 7 days or is not someday
//     }

//     return todosByDay;
// }

function isBranchComplete(
  todoId: TodoId,
  todoIdMap: TodoIdMap,
  parentChildMapCache?: { [parentTodoId: TodoId]: Todo[] },
): boolean {
  let todo = todoIdMap[todoId];
  if (!todo) {
    return false;
  }

  let root = todo;
  const isRoot = todo.parent == null;
  // only root todos are considered branch roots
  if (!isRoot) {
    let rootFound = rootOfTodo(todoIdMap, todoId);
    if (!rootFound) {
      return false;
    }
    root = rootFound;
  }

  let deepChildren = TodoUtils.deepChildrenOfTodo(
    todoIdMap,
    root.id,
    parentChildMapCache,
  );
  let toCheckTodos = [...deepChildren, root]; // all need to be complete

  for (let todo of toCheckTodos) {
    if (!todo.completed) {
      // this tree is not complete yet, do not delete
      return false;
    }
  }
  return true;
}

export function hashtagsOrdered(
  todoIdMap: TodoIdMap,
  hashtagMetas: HashtagMeta[],
) {
  const hashtags = new Set<string>();
  Object.values(TodoUtils.withoutDeleted(todoIdMap))
    .flatMap((todo) => todo.meta?.filter(isHashtagMetaItem) ?? [])
    .map((h) => h.value)
    .forEach((h) => hashtags.add(h));

  hashtagMetas.forEach((meta) => hashtags.add(meta.hashtag));

  const hashtagOrderMap = new Map(
    hashtagMetas.map((meta) => [meta.hashtag, meta.order]),
  );

  const sortedHashtags = Array.from(hashtags).sort((a, b) => {
    const orderA = hashtagOrderMap.get(a) ?? Infinity;
    const orderB = hashtagOrderMap.get(b) ?? Infinity;
    return orderA - orderB || a.localeCompare(b);
  });

  return sortedHashtags;
}

export function hashtagOrderedByTodoLastEdit(todoIdMap: TodoIdMap) {
  const sortedHashtags: string[] = [];
  const addedHashtags = new Set<string>();

  Object.values(TodoUtils.withoutDeleted(todoIdMap))
    .sort((a, b) => b.lastEditDate - a.lastEditDate)
    .map((todo) => todo.meta?.filter(isHashtagMetaItem) ?? [])
    .reverse() // reverse so that last hashtags are considered most recent
    .flat()
    .forEach((hashtag) => {
      if (!addedHashtags.has(hashtag.value)) {
        sortedHashtags.push(hashtag.value);
        addedHashtags.add(hashtag.value);
      }
    });

  return sortedHashtags;
}

/**
 * consolidates a full flat list of todos from outermost todos list
 * @param todoList
 */
export function flattenTodoTree(
  outermostTodosList: Todo[],
  childrenMap: { [id: string]: Todo[] },
): Todo[] {
  let finalFlattenedTodos = [];
  let sortedOutermost = sortedTodos(outermostTodosList);

  //inject indentation
  sortedOutermost = sortedOutermost.map((todo) => ({ ...todo, indent: 0 }));
  let queue = [...sortedOutermost];
  while (queue.length != 0) {
    let todo = queue.shift() as Todo;
    finalFlattenedTodos.push(todo);
    if (!todo.collapsed) {
      let children = sortedTodos(childrenMap[todo.id] || []);
      //inject indentation
      children = children.map((eachTodo) => ({
        ...eachTodo,
        indent: todo.indent + 1,
      }));
      queue = [...children, ...queue]; //insert children at front - dfs
    }
  }

  return finalFlattenedTodos;
}

export function withoutDeleted(todoIdMap: TodoIdMap) {
  let newTodoIdMap: TodoIdMap = {};
  for (const [todoId, todo] of Object.entries(todoIdMap)) {
    if (todo && !todo.deleted) {
      newTodoIdMap[todoId] = todo;
    }
  }
  return newTodoIdMap;
}
//========================================================================
//
//                            Utility Functions
//
//========================================================================
function mergeTodo<T extends Partial<Todo>>(
  oldTodo: T,
  newTodo: Partial<Todo>,
): T {
  // TODO: merge meta data
  // const mergedMeta = [...(oldTodo.meta ?? []), ...(newTodo.meta ?? [])];
  return { ...oldTodo, ...newTodo };
}

function mod(a: number, n: number) {
  return a - n * Math.floor(a / n);
}

/**
 *
 * @param sameLevelTodoList list of todos, not necessarily of a single date
 * @returns
 */
function sortedTodos(sameLevelTodoList: Todo[], asc: boolean = true) {
  let sorted = [...sameLevelTodoList].sort((a, b) => {
    if (a.lexidx > b.lexidx) {
      return asc ? 1 : -1;
    } else if (a.lexidx < b.lexidx) {
      return asc ? -1 : 1;
    } else {
      return 0;
    }
  });

  sorted = [...sorted].sort((a, b) => {
    let aStartOfDay =
      a.date === null ? 0 : moment(a.date).startOf("day").valueOf();
    let bStartOfDay =
      b.date === null ? 0 : moment(b.date).startOf("day").valueOf();
    if (aStartOfDay > bStartOfDay) {
      return asc ? 1 : -1;
    } else if (aStartOfDay < bStartOfDay) {
      return asc ? -1 : 1;
    } else {
      return 0;
    }
  });
  return sorted;
}

/**
 * As opposed to sortedTodos, orderedTodos take into account parent child relationships
 * Assumes that all todos are same date
 * @param todoList
 */
function orderedTodos(todoList: Todo[]) {
  const pcMap = parentChildMap(todoList);
  const allTodoIds = new Set([...todoList.map((t) => t.id)]);

  // not exactly the right implementation,  but we don't have enough information
  // to truly identify what level of hierarchy the todos provided are.
  // as such, we simply assume that if their parent is in the list, they should be indented
  const rootTodos = todoList.filter(
    (t) => t.parent === null || !allTodoIds.has(t.parent),
  );

  return flattenTodoTree(rootTodos, pcMap);
}

function reindexTodos(sortedTodoList: TodoUpdateDelta[]): TodoUpdateDeltaIdMap {
  let delta: TodoUpdateDeltaIdMap = {};

  for (let i = 0; i < sortedTodoList.length; i++) {
    const lexidx = lexi.pack(INIT_LEX_IDX + i * DEFAULT_LEX_IDX_OFFSET, "hex");

    const todo = sortedTodoList[i]!;
    delta[todo.id] = { id: todo.id, lexidx };
  }

  return delta;
}

function toTree(
  todoList: Todo[],
  parentId: string | null = null,
  parentToChildren = parentChildMap(todoList),
): TreeTodo[] {
  const outerTodos = todoList.filter((todo) => todo.parent === parentId);
  const tree: TreeTodo[] = [];

  for (let todo of outerTodos) {
    const innerTodos = parentToChildren[todo.id];
    const innerTreeTodos = innerTodos
      ? toTree(innerTodos, todo.id, parentToChildren /* don't recompute */)
      : [];
    tree.push({ ...todo, children: innerTreeTodos });
  }

  return tree;
}

/**
 * @returns todo id map for those that need to be updated
 */
export function checkCleanParentChildRelation(todoIdMap: TodoIdMap): TodoIdMap {
  let outermostTodos = Object.values(todoIdMap);

  let toUpdate: TodoIdMap = {};
  for (let parent of outermostTodos) {
    const children = deepChildrenOfTodo(todoIdMap, parent.id);
    for (let child of children) {
      if (child.date != parent.date) {
        toUpdate[child.id] = { ...child, date: parent.date };
      }
    }
  }

  return toUpdate;
}

export function isSameDay(
  dateA: Date | number | null | Moment | undefined,
  dateB: Date | number | null | Moment | undefined,
) {
  // undefined will be mapped to "today"
  if ((dateA == undefined || dateB == undefined) && dateA != dateB)
    return false;
  if (dateA === dateB) return true;
  return moment(dateA).isSame(moment(dateB), "day");
}

export function isFullTodo(todo: Partial<Todo> | undefined): todo is Todo {
  if (!todo) return false;
  const templateTodo = createTodo({ owner: "", space: "" });
  const keys = Object.keys(templateTodo);
  for (let key of keys) {
    const isRequired = isDefined(templateTodo[key as keyof Todo]);
    if (isRequired && todo[key as keyof Todo] === undefined) {
      console.warn("todo missing key", key, "id:", todo.id);
      return false;
    }
  }
  return (
    todo.id !== undefined &&
    todo.owner !== undefined &&
    todo.space !== undefined
  );
}
function passFilterFn(
  todo: Todo,
  filterFn: FilterFunction,
  todoIdMap: TodoIdMap,
) {
  // Requirements:
  // 1. Include if todo has a child/self passes the positive filter
  // 2. Else, Exclude if there is a negative filter, and self fails
  // 3. Else, Exclude if there is a positive filter, and self fails
  const deepChildren = TodoUtils.deepChildrenOfTodo(todoIdMap, todo.id);
  const {
    passNegativeFilter,
    hasNegativeFilter,
    passPositiveFilter,
    hasPositiveFilter,
  } = filterFn(todo);

  // 1. Include if todo has a child/self passes the positive filter
  if (hasPositiveFilter && passPositiveFilter) return true;

  // 1b
  for (let child of deepChildren) {
    const { passPositiveFilter, hasPositiveFilter } = filterFn(child);
    if (hasPositiveFilter && passPositiveFilter) return true;
  }

  // 2. Else, Exclude if self fails the negative filter
  if (hasNegativeFilter && !passNegativeFilter) return false;

  // 3. Else, Exclude if there is a positive filter, and self fails
  if (hasPositiveFilter && !passPositiveFilter) return false;

  return true;
}

/**
 * 1. If mark complete, and branch is complete, move to today
 * 2. If mark complete, and has parent, make todo last of it's siblings
 * 3. If mark incomplete, and has parent, make todo last of it's incomplete siblings
 */
export function markComplete(
  todoId: TodoId,
  completed: boolean,
  todoIdMap: TodoIdMap,
  moveToTodayOnComplete: boolean,
): TodoUpdateDeltaIdMap {
  const todo = todoIdMap[todoId];

  if (!todo) throw new Error("Todo not found");

  const updateIdMap: TodoUpdateDeltaIdMap = {};

  const todoUpdateDelta: TodoUpdateDelta = {
    id: todoId,
    completed,
    ...(completed && todo.reminder ? { reminder: null } : {}),
  };

  if (todo.parent) {
    let newNumIdx;
    const sameLevelTodos = TodoUtils.sortedTodos(
      Object.values(todoIdMap).filter(
        (t) => t.parent === todo.parent && t.id !== todoId,
      ),
    );

    if (completed && sameLevelTodos.length) {
      newNumIdx =
        lexi.unpack(sameLevelTodos[sameLevelTodos.length - 1]!.lexidx) +
        DEFAULT_LEX_IDX_OFFSET;
    }

    if (!completed && sameLevelTodos.length) {
      const incompleteSiblings = sameLevelTodos.filter((t) => !t.completed);
      let lastIncompleteSibling =
        incompleteSiblings[incompleteSiblings.length - 1];
      let firstCompleteSibling = sameLevelTodos.find((t) => t.completed);

      if (!lastIncompleteSibling && firstCompleteSibling) {
        // insert before first complete sibling
        newNumIdx =
          lexi.unpack(firstCompleteSibling.lexidx) - DEFAULT_LEX_IDX_OFFSET;
      } else if (lastIncompleteSibling && !firstCompleteSibling) {
        // insert after last incomplete sibling
        newNumIdx =
          lexi.unpack(lastIncompleteSibling.lexidx) + DEFAULT_LEX_IDX_OFFSET;
      } else if (lastIncompleteSibling && firstCompleteSibling) {
        const firstCompleteNumIdx = lexi.unpack(firstCompleteSibling.lexidx);
        const lastIncompleteNumIdx = lexi.unpack(lastIncompleteSibling.lexidx);
        const isInvalidOrder = lastIncompleteNumIdx > firstCompleteNumIdx;
        if (!isInvalidOrder) {
          // insert between first complete sibling and last incomplete sibling
          newNumIdx = (firstCompleteNumIdx + lastIncompleteNumIdx) / 2;
        }
      }
    }

    if (newNumIdx) todoUpdateDelta.lexidx = lexi.pack(newNumIdx, "hex");
  }

  updateIdMap[todoId] = todoUpdateDelta;

  const updatedTodoIdMap = mergeLeftIdMaps(todoIdMap, updateIdMap);

  const isBranchComplete = TodoUtils.isBranchComplete(todoId, updatedTodoIdMap);
  const isToday = TodoUtils.isSameDay(
    updatedTodoIdMap[todoId]?.date,
    new Date(),
  );
  if (moveToTodayOnComplete && isBranchComplete && !isToday) {
    const rootId = TodoUtils.rootOfTodo(updatedTodoIdMap, todoId)?.id ?? todoId;
    const today = moment().startOf("day").valueOf();
    const todoUpdateDeltaIdMap = TodoUtils.moveTodosToGroup(
      updatedTodoIdMap,
      [rootId],
      PRESET_GROUP_KEYS.TODAY,
      "default", // Force default view so we can be sure there is a "today" column
      nullFilterFn,
    );

    return withMatchingChildDates(
      mergeLeftPartialIdMaps(updateIdMap, todoUpdateDeltaIdMap),
      updatedTodoIdMap,
    );
  }
  return updateIdMap;
}

export function stringifyTodos(todos: Todo[]) {
  let str = "";
  let lastDate = undefined;
  const todoList = TodoUtils.orderedTodos(todos);
  for (let todo of todoList) {
    if (!TodoUtils.isSameDay(todo.date, lastDate)) {
      const isFirst = todoList[0] === todo;
      if (!isFirst) {
        str += "\n"; // break between dates
      }
      const isSomeday = todo.date == null;
      str += isSomeday
        ? "**Someday**\n"
        : `**${moment(todo.date).format("ddd, DD MMM")}**\n`;
      lastDate = todo.date;
    }
    for (let i = 0; i < todo.indent; i++) {
      str += "\t";
    }
    str += todo.completed ? "☑︎" : "□";
    str += " " + todo.value + "\n";
  }
  return str;
}

export function extractTodos(todoIds: TodoId[], todoState: TodoIdMap): Todo[] {
  return todoIds
    .map((id) => todoState[id])
    .filter((t): t is Todo => {
      if (!t) console.trace("Attempted to extract todo that does not exist");
      return !!t;
    });
}

export function objectMap(todoIdMap: TodoIdMap, fn: (todo: Todo) => Todo) {
  return Object.entries(todoIdMap).reduce((acc, [todoId, todo]) => {
    acc[todoId] = fn(todo);
    return acc;
  }, {} as TodoIdMap);
}

const TodoUtils = {
  backlogToSomeday,
  checkCleanParentChildRelation,
  childrenOfTodo,
  collapsedParentOfTodo,
  createTodo,
  deepChildrenOfTodo,
  dueTodosByDay,
  fillTodosState: initTodosState,
  getCompletedTodosForDate,
  groupInDirection,
  groupKeysOrdered,
  hardDeleteTodo,
  hardDeleteTodoDeep,
  hashtagOrderedByTodoLastEdit,
  hashtagsOrdered,
  indentTodo,
  insertTodosAfter,
  insertTodosBefore,
  isBranchComplete,
  isDateBasedGroup,
  isFlaggable,
  isFullTodo,
  isSameDay,
  markComplete,
  mergeLeftIdMaps,
  mergeTodo,
  migrateTodos,
  moveTodoInDirection,
  moveTodosToGroup,
  moveTodoVertical,
  newTodo,
  nextTodo,
  objectMap,
  orderedTodos,
  parentChildMap,
  passFilterFn,
  previousTodo,
  reindexIfNeeded,
  reindexTodos,
  rolloverBacklog,
  rolloverYstd,
  rootOfTodo,
  sameLevelTodosSortedInGroup,
  softDeleteTodo,
  softDeleteTodoDeep,
  sortedTodos,
  stringifyTodos,
  toTree,
  visibleChildrenOfTodo,
  withHeaders,
  withoutDeleted,
};
export default TodoUtils;
